import { useState } from "react";
import {
    Badge,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
    alpha,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import { VentaDraft } from "../hooks/use-venta-draft";

interface Props {
    drafts: VentaDraft[];
    onRestore: (draft: VentaDraft) => void;
    onDelete: (id: string) => void;
}

export default function VentaDraftRestore({ drafts, onRestore, onDelete }: Props) {
    const [open, setOpen] = useState(false);

    if (drafts.length === 0) return null;

    const handleRestore = (draft: VentaDraft) => {
        setOpen(false);
        onRestore(draft);
    };

    const handleDelete = (id: string) => {
        onDelete(id);
        if (drafts.length <= 1) setOpen(false);
    };

    return (
        <>
            <Badge badgeContent={drafts.length} color="warning">
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RestoreIcon />}
                    onClick={() => setOpen(true)}
                >
                    Borradores
                </Button>
            </Badge>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1.5 }}>
                    <Stack flex={1}>
                        <Typography variant="h6" component="span" fontWeight={600}>Borradores guardados</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Ventas que quedaron sin confirmar y podés retomar
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={() => setOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 0 }}>
                    <Stack>
                        {drafts.map((draft, index) => (
                            <Stack key={draft.id}>
                                {index > 0 && <Divider />}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={2}
                                    sx={{
                                        p: 2,
                                        transition: "background-color 0.15s ease",
                                        "&:hover": {
                                            backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
                                        },
                                    }}
                                >
                                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" noWrap>
                                            {draft.clienteNombre ?? "Sin cliente"}
                                        </Typography>
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                                {`${draft.itemCount} artículo${draft.itemCount !== 1 ? "s" : ""}`}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {dayjs(draft.savedAt).format("DD/MM/YYYY HH:mm")}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <Stack direction="row" gap={1} flexShrink={0}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<RestoreIcon fontSize="small" />}
                                            onClick={() => handleRestore(draft)}
                                        >
                                            Restaurar
                                        </Button>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(draft.id)}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
}
